import React, { useState, useEffect } from "react";
import { getCalApi } from "@calcom/embed-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Loader2, X } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const SYNC_POINTS = [
  "Expert names and companies filled in from the invite",
  "Network provider identified automatically",
  "Dates, times, and durations extracted",
  "Works with the Outlook calendar you already use",
];

const CLEAN_POINTS = [
  "Filler words removed, mis-transcriptions corrected",
  "Speakers labeled throughout",
  "The expert's meaning preserved, not paraphrased",
  "Batches process in parallel with live progress",
];

const FEATURES = [
  {
    title: "Calendar sync",
    desc: "A week of interviews logged in one click, straight from Outlook.",
  },
  {
    title: "Transcript cleaning",
    desc: "Raw transcripts become client-ready documents automatically.",
  },
  {
    title: "Interview chat",
    desc: "Ask questions in plain English, answered from your transcripts.",
  },
  {
    title: "Multi-project tracking",
    desc: "Every campaign across the firm, visible in one place.",
  },
  {
    title: "Cost analytics",
    desc: "Spend by project, network, and week, tracked as calls happen.",
  },
  {
    title: "Batch export",
    desc: "Download every cleaned transcript as formatted DOCX files.",
  },
  {
    title: "Live status",
    desc: "See processing progress in real time, not after a refresh.",
  },
  {
    title: "Project sharing",
    desc: "Give leadership read access without another status email.",
  },
];

const ProductInfo: React.FC = () => {
  const navigate = useNavigate();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  usePageMeta(
    "Ivy — Expert Interview Management for Research-Driven Firms",
    "Ivy syncs expert interview details from your calendar, cleans transcripts with AI, and answers questions across every interview your firm has run."
  );

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: "ivy-demo" });
      cal("ui", {
        theme: "light",
        cssVarsPerTheme: {
          light: { "cal-brand": "#0F766E" },
          dark: { "cal-brand": "#0F766E" },
        },
        hideEventTypeDetails: false,
        layout: "month_view",
      });
    })();
  }, []);

  const handleGoHome = () => {
    navigate("/signin");
  };

  const openContactModal = () => {
    setIsContactOpen(true);
    setIsSubmitted(false);
  };

  const closeContactModal = () => {
    setIsContactOpen(false);
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch("https://formspree.io/f/xkglneae", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setIsSubmitted(true);
        form.reset();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#1F2823] font-schibsted overflow-x-hidden">
      {/* Navigation */}
      <nav className="border-b border-[#E5E1D6]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/Ivy-logo.png" alt="" className="h-8 w-auto" />
            <span className="font-spectral font-semibold text-2xl tracking-tight">
              Ivy
            </span>
          </div>
          <div className="flex items-center gap-6">
            <button
              className="hidden sm:block text-sm text-[#5B655E] hover:text-[#1F2823] transition-colors"
              data-cal-namespace="ivy-demo"
              data-cal-link="jonjas/15min"
              data-cal-config='{"layout":"month_view","theme":"light"}'
            >
              Book a demo
            </button>
            <Button
              onClick={handleGoHome}
              variant="outline"
              className="border-[#1F2823]/25 bg-transparent text-[#1F2823] hover:bg-[#1F2823] hover:text-[#FAF9F5] rounded-md transition-colors"
            >
              Sign in
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-6 pt-20 md:pt-28 pb-16">
        <div className="max-w-3xl">
          <h1 className="animate-fade-up font-spectral font-medium text-[2.75rem] leading-[1.08] md:text-6xl tracking-tight mb-7">
            The system of record for expert interviews.
          </h1>
          <p className="animate-fade-up delay-100 text-lg md:text-xl text-[#5B655E] leading-relaxed max-w-2xl mb-10">
            Ivy reads expert names, networks, and call details out of your
            calendar, turns raw transcripts into clean documents, and answers
            questions across every interview your firm has run.
          </p>
          <div className="animate-fade-up delay-200 flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              className="bg-[#0F766E] hover:bg-[#0B5D57] text-white rounded-md px-7 h-12 text-base font-medium"
              data-cal-namespace="ivy-demo"
              data-cal-link="jonjas/15min"
              data-cal-config='{"layout":"month_view","theme":"light"}'
            >
              Book a 15-minute demo
            </Button>
            <Button
              onClick={handleGoHome}
              size="lg"
              variant="outline"
              className="border-[#1F2823]/25 bg-transparent text-[#1F2823] hover:bg-[#1F2823]/5 rounded-md px-7 h-12 text-base font-medium"
            >
              Get started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="animate-fade-up delay-300 mt-16">
          <div className="rounded-lg overflow-hidden border border-[#E5E1D6] shadow-[0_1px_2px_rgba(31,40,35,0.06),0_8px_24px_rgba(31,40,35,0.07)]">
            <img
              src="/Ivy_dashboard_ss_light_mode.png"
              alt="The Ivy dashboard showing an interview campaign"
              className="w-full h-auto"
            />
          </div>
        </div>
      </header>

      {/* Stat strip */}
      <section className="border-y border-[#E5E1D6]">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 sm:divide-x divide-[#E5E1D6]">
          <div className="py-12 sm:pr-12">
            <p className="font-spectral text-5xl md:text-6xl font-medium tracking-tight mb-3">
              3+ hours
            </p>
            <p className="text-[#5B655E] leading-relaxed">
              spent each week logging interview details by hand, per case team.
            </p>
          </div>
          <div className="py-12 border-t sm:border-t-0 border-[#E5E1D6] sm:pl-12">
            <p className="font-spectral text-5xl md:text-6xl font-medium tracking-tight text-[#0F766E] mb-3">
              4 seconds
            </p>
            <p className="text-[#5B655E] leading-relaxed">
              for Ivy to sync the same interviews from your calendar.
            </p>
          </div>
        </div>
      </section>

      {/* Calendar sync */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <h2 className="font-spectral font-medium text-3xl md:text-4xl tracking-tight leading-tight mb-5">
              Logged straight from your calendar.
            </h2>
            <p className="text-[#5B655E] text-lg leading-relaxed mb-8">
              Connect Outlook once. When an expert call lands on your calendar,
              Ivy reads the invite and records the interview for you. A full
              week of calls syncs in one click.
            </p>
            <ul className="border-t border-[#E5E1D6]">
              {SYNC_POINTS.map((item) => (
                <li
                  key={item}
                  className="flex items-baseline gap-3 py-3.5 border-b border-[#E5E1D6] text-[15px]"
                >
                  <span className="h-1.5 w-1.5 bg-[#0F766E] flex-shrink-0 translate-y-[-2px]" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg overflow-hidden border border-[#E5E1D6]">
            <img
              src="/calendarsync.gif"
              alt="Calendar sync importing interviews"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Transcript cleaning */}
      <section className="bg-[#F2F0E9] border-y border-[#E5E1D6]">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 space-y-4">
              <div className="bg-[#FAF9F5] rounded-lg border border-[#E5E1D6] p-5">
                <p className="text-xs uppercase tracking-[0.12em] text-[#8A938C] mb-3">
                  Raw transcript
                </p>
                <p className="text-[#5B655E] text-[15px] leading-relaxed">
                  "So, um, basically what we're seeing is, you know, like the
                  market is shifting and, uh, competitors are, you know, trying
                  to..."
                </p>
              </div>
              <div className="bg-white rounded-lg border border-[#0F766E]/30 p-5">
                <p className="text-xs uppercase tracking-[0.12em] text-[#0F766E] mb-3">
                  Cleaned
                </p>
                <p className="text-[15px] leading-relaxed">
                  <span className="font-semibold">Expert:</span> The market is
                  shifting and competitors are trying to capture share through
                  aggressive pricing strategies...
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-spectral font-medium text-3xl md:text-4xl tracking-tight leading-tight mb-5">
                Transcripts you can hand to a client.
              </h2>
              <p className="text-[#5B655E] text-lg leading-relaxed mb-8">
                Upload the raw file and Ivy removes the filler, labels the
                speakers, and fixes mis-transcriptions while keeping what the
                expert actually said.
              </p>
              <ul className="border-t border-[#E5E1D6]">
                {CLEAN_POINTS.map((item) => (
                  <li
                    key={item}
                    className="flex items-baseline gap-3 py-3.5 border-b border-[#E5E1D6] text-[15px]"
                  >
                    <span className="h-1.5 w-1.5 bg-[#0F766E] flex-shrink-0 translate-y-[-2px]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Interview chat */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <h2 className="font-spectral font-medium text-3xl md:text-4xl tracking-tight leading-tight mb-5">
            Ask questions across every interview.
          </h2>
          <p className="text-[#5B655E] text-lg leading-relaxed">
            Each transcript is indexed as soon as it's cleaned. Ask in plain
            English and Ivy answers from the interviews themselves, with the
            sources it drew on.
          </p>
        </div>

        <div className="max-w-3xl">
          <div className="bg-white rounded-lg border border-[#E5E1D6] p-6 md:p-8">
            <div className="flex items-center justify-between pb-4 mb-6 border-b border-[#E5E1D6]">
              <span className="text-xs uppercase tracking-[0.12em] text-[#8A938C]">
                Ivy chat
              </span>
              <span className="text-xs text-[#8A938C]">
                47 interviews indexed
              </span>
            </div>
            <div className="space-y-6">
              <div className="flex justify-end">
                <div className="bg-[#0F766E] text-white px-5 py-3 rounded-lg rounded-br-sm max-w-md">
                  <p className="text-sm leading-relaxed">
                    What are the main challenges customers face with current
                    solutions?
                  </p>
                </div>
              </div>
              <div className="flex justify-start">
                <div className="bg-[#F2F0E9] px-5 py-4 rounded-lg rounded-bl-sm max-w-lg">
                  <p className="text-sm leading-relaxed mb-3">
                    Based on{" "}
                    <span className="text-[#0F766E] font-semibold">
                      23 expert interviews
                    </span>{" "}
                    across 4 projects, the top challenges are:
                  </p>
                  <ol className="space-y-2 text-sm list-decimal list-inside">
                    <li>
                      <span className="font-semibold">
                        Integration complexity
                      </span>
                      , mentioned in 78% of interviews
                    </li>
                    <li>
                      <span className="font-semibold">
                        Pricing transparency
                      </span>
                      , cited by 12 experts as a barrier
                    </li>
                    <li>
                      <span className="font-semibold">
                        Support responsiveness
                      </span>
                      , a common pain point in enterprise...
                    </li>
                  </ol>
                  <p className="text-xs text-[#8A938C] mt-4 pt-3 border-t border-[#E5E1D6]">
                    Sources: 3 healthcare experts, 5 fintech executives, 2
                    retail CTOs
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two audiences */}
      <section className="border-y border-[#E5E1D6] bg-[#F2F0E9]">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <h2 className="font-spectral font-medium text-3xl md:text-4xl tracking-tight leading-tight mb-14 max-w-2xl">
            For the people running the work, and the people accountable for
            it.
          </h2>
          <div className="grid md:grid-cols-2 md:divide-x divide-[#E5E1D6]">
            <div className="pb-10 md:pb-0 md:pr-12">
              <h3 className="font-spectral font-medium text-2xl mb-2">
                Case teams
              </h3>
              <p className="text-[#0F766E] font-medium text-sm mb-4">
                Less data entry, more analysis
              </p>
              <p className="text-[#5B655E] leading-relaxed mb-6">
                Stop copying interview details between systems. Sync the
                calendar, upload the transcript, and get back to the work the
                client is paying for.
              </p>
              <ul className="space-y-2.5 text-[15px]">
                {[
                  "One-click calendar sync",
                  "Automatic transcript cleaning",
                  "Budget tracking as calls happen",
                  "Daily schedule views",
                ].map((item) => (
                  <li key={item} className="flex items-baseline gap-3">
                    <span className="h-1.5 w-1.5 bg-[#0F766E] flex-shrink-0 translate-y-[-2px]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="pt-10 md:pt-0 border-t md:border-t-0 border-[#E5E1D6] md:pl-12">
              <h3 className="font-spectral font-medium text-2xl mb-2">
                Leadership
              </h3>
              <p className="text-[#0F766E] font-medium text-sm mb-4">
                Every campaign in one place
              </p>
              <p className="text-[#5B655E] leading-relaxed mb-6">
                See spending, pacing, and findings across all interview
                campaigns without chasing case teams for status updates.
              </p>
              <ul className="space-y-2.5 text-[15px]">
                {[
                  "Oversight across projects",
                  "Questions answered from the transcripts",
                  "Cost analytics and trends",
                  "Shared read access, no extra seats of work",
                ].map((item) => (
                  <li key={item} className="flex items-baseline gap-3">
                    <span className="h-1.5 w-1.5 bg-[#0F766E] flex-shrink-0 translate-y-[-2px]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Feature index */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <h2 className="font-spectral font-medium text-3xl md:text-4xl tracking-tight leading-tight mb-12">
          What's in the platform
        </h2>
        <div className="grid sm:grid-cols-2 gap-x-16 border-t border-[#E5E1D6] sm:border-t-0">
          {FEATURES.map((feature, idx) => (
            <div
              key={feature.title}
              className={`py-5 border-b border-[#E5E1D6] ${
                idx < 2 ? "sm:border-t sm:border-t-[#E5E1D6]" : ""
              }`}
            >
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="font-semibold text-[17px]">{feature.title}</h3>
              </div>
              <p className="text-[#5B655E] text-[15px] leading-relaxed mt-1">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Founder note */}
      <section className="border-t border-[#E5E1D6]">
        <div className="max-w-3xl mx-auto px-6 py-24">
          <div className="flex flex-col sm:flex-row items-start gap-8">
            <img
              src="/jjheadshot_nobackground.png"
              alt="Jonathan Jasica, founder of Ivy"
              className="h-24 w-24 rounded-full object-cover border border-[#E5E1D6] flex-shrink-0"
            />
            <div>
              <p className="font-spectral text-2xl md:text-[1.7rem] leading-snug mb-6">
                "I built Ivy after spending too many hours copying interview
                details into spreadsheets. The busywork is gone, the team
                focuses on what the experts actually said, and leadership can
                see every campaign in one place."
              </p>
              <p className="text-[#5B655E]">
                <span className="font-semibold text-[#1F2823]">
                  Jonathan Jasica
                </span>
                , founder of Ivy
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1F2823] text-[#FAF9F5]">
        <div className="max-w-6xl mx-auto px-6 py-24 text-center">
          <h2 className="font-spectral font-medium text-4xl md:text-5xl tracking-tight leading-tight mb-5">
            See your interviews in Ivy.
          </h2>
          <p className="text-[#FAF9F5]/70 text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            A 15-minute walkthrough with the founder, using your workflow as
            the example.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              className="bg-[#0F766E] hover:bg-[#0B5D57] text-white rounded-md px-8 h-12 text-base font-medium"
              data-cal-namespace="ivy-demo"
              data-cal-link="jonjas/15min"
              data-cal-config='{"layout":"month_view","theme":"light"}'
            >
              Book a demo
            </Button>
            <Button
              onClick={handleGoHome}
              size="lg"
              variant="outline"
              className="border-[#FAF9F5]/30 bg-transparent text-[#FAF9F5] hover:bg-[#FAF9F5]/10 hover:text-[#FAF9F5] rounded-md px-8 h-12 text-base font-medium"
            >
              Get started
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E1D6] bg-[#FAF9F5]">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/Ivy-logo.png" alt="" className="h-5 w-auto" />
              <span className="text-[#8A938C] text-sm">
                © {new Date().getFullYear()} Ivy Digital LLC
              </span>
            </div>
            <div className="flex items-center gap-6">
              <button
                onClick={openContactModal}
                className="text-[#8A938C] hover:text-[#0F766E] text-sm transition-colors"
              >
                Contact
              </button>
              <a
                href="/privacy"
                className="text-[#8A938C] hover:text-[#0F766E] text-sm transition-colors"
              >
                Privacy
              </a>
              <a
                href="/terms"
                className="text-[#8A938C] hover:text-[#0F766E] text-sm transition-colors"
              >
                Terms
              </a>
              <a
                href="https://www.linkedin.com/company/ivytrack/about/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#8A938C] hover:text-[#0F766E] transition-colors"
                aria-label="LinkedIn"
              >
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {isContactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-[#1F2823]/40"
            onClick={closeContactModal}
          />

          <div className="relative w-full max-w-md animate-scale-in">
            <div className="bg-[#FAF9F5] rounded-lg border border-[#E5E1D6] shadow-[0_8px_32px_rgba(31,40,35,0.16)] p-8">
              <button
                onClick={closeContactModal}
                className="absolute top-4 right-4 text-[#8A938C] hover:text-[#1F2823] transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {isSubmitted ? (
                <div className="text-center py-4">
                  <CheckCircle2 className="h-10 w-10 text-[#0F766E] mx-auto mb-4" />
                  <h3 className="font-spectral font-medium text-2xl mb-2">
                    Message sent
                  </h3>
                  <p className="text-[#5B655E] mb-6">
                    Thanks for reaching out. You'll hear back within a day.
                  </p>
                  <Button
                    onClick={closeContactModal}
                    variant="outline"
                    className="border-[#1F2823]/25 bg-transparent text-[#1F2823] hover:bg-[#1F2823]/5 rounded-md"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="font-spectral font-medium text-2xl mb-2">
                    Get in touch
                  </h3>
                  <p className="text-[#5B655E] text-sm mb-6">
                    Questions about Ivy? You'll hear back within a day.
                  </p>

                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-medium mb-1.5"
                      >
                        Name
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full bg-white border border-[#E5E1D6] rounded-md py-2.5 px-3.5 text-[#1F2823] placeholder-[#8A938C] focus:outline-none focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] transition-colors"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-sm font-medium mb-1.5"
                      >
                        Work email
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        placeholder="you@company.com"
                        className="w-full bg-white border border-[#E5E1D6] rounded-md py-2.5 px-3.5 text-[#1F2823] placeholder-[#8A938C] focus:outline-none focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] transition-colors"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-company"
                        className="block text-sm font-medium mb-1.5"
                      >
                        Company{" "}
                        <span className="text-[#8A938C] font-normal">
                          (optional)
                        </span>
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        name="company"
                        className="w-full bg-white border border-[#E5E1D6] rounded-md py-2.5 px-3.5 text-[#1F2823] placeholder-[#8A938C] focus:outline-none focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] transition-colors"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-message"
                        className="block text-sm font-medium mb-1.5"
                      >
                        Message
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={3}
                        className="w-full bg-white border border-[#E5E1D6] rounded-md py-2.5 px-3.5 text-[#1F2823] placeholder-[#8A938C] focus:outline-none focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] transition-colors resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#0F766E] hover:bg-[#0B5D57] text-white rounded-md h-11 font-medium disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send message"
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductInfo;
